/**
 * Metrics Tab Component
 *
 * Shows all metrics sections: Query Performance, Retrieval Quality,
 * Document Classification, Cost & Usage, and Data Ingestion.
 */

import { useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MetricsCards,
  IngestionMetricsCards,
  SyncWorkerMetricsCards,
  ClassificationMetricsCards,
  ClassificationTierChart,
  QueryVolumeChart,
  LatencyTrendsChart,
  IngestionChart,
  ErrorDistributionChart,
  SyncOutcomesChart,
  RadialProgress,
  PostFilterRejectionsChart,
  TokenUsageChart,
  TimeRangeSelector,
  METRIC_DESCRIPTIONS,
} from '@/components/Dashboard';
import { DashboardSection } from './DashboardSection';
import type { TimeRange, MetricsResponse } from '@/types/dashboard';

interface MetricsTabProps {
  data: MetricsResponse | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
}

export function MetricsTab({
  data,
  isLoading,
  error,
  lastUpdated,
  timeRange,
  onTimeRangeChange,
}: MetricsTabProps) {
  // Calculate totalErrors once to avoid redundant computation
  const totalErrors = useMemo(
    () => data?.errorsByType?.reduce((sum, e) => sum + e.count, 0),
    [data?.errorsByType],
  );

  return (
    <div className="space-y-6">
      {/* Header with Time Range Selector */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-muted-foreground h-5 flex items-center">
          {lastUpdated ? (
            <>
              Last updated: {lastUpdated.toLocaleTimeString()}
              <span className="ml-2 text-xs">(auto-refreshes)</span>
            </>
          ) : (
            <Skeleton className="h-4 w-56" />
          )}
        </div>
        <TimeRangeSelector
          value={timeRange}
          onChange={onTimeRangeChange}
          disabled={isLoading}
        />
      </div>

      {/* Error Display */}
      {error && (
        <div
          className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg"
          role="alert"
        >
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Section 1: Query Performance */}
      <DashboardSection
        id="query-performance"
        title="Query Performance"
        description="Monitor query volume, response times, and error rates"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <QueryVolumeChart
            queryData={data?.timeSeries.queryVolume}
            errorData={data?.timeSeries.errors}
            isLoading={isLoading}
            totalQueries={data?.summary?.totalQueries}
            totalErrors={totalErrors}
          />
          <LatencyTrendsChart
            retrievalData={data?.timeSeries.retrievalLatency}
            llmData={data?.timeSeries.llmLatency}
            ttftData={data?.timeSeries.timeToFirstToken}
            isLoading={isLoading}
            avgRetrievalLatency={data?.summary?.avgLatencyMs}
            avgLlmLatency={data?.latencyPercentiles?.llm?.p50}
            avgTtft={data?.summary?.avgTimeToFirstTokenMs}
          />
        </div>
        <ErrorDistributionChart
          data={data?.errorsByType}
          isLoading={isLoading}
          errorRate={data?.summary?.errorRate}
          totalErrors={totalErrors}
        />
      </DashboardSection>

      {/* Section 2: Retrieval Quality */}
      <DashboardSection
        id="retrieval-quality"
        title="Retrieval Quality"
        description="Measure how effectively the system finds and returns relevant documents"
      >
        <MetricsCards data={data?.summary} isLoading={isLoading} />
        <PostFilterRejectionsChart
          data={data?.timeSeries.postFilterRejections}
          isLoading={isLoading}
          rejectionRate={data?.summary?.postFilterRejectionRate}
        />
      </DashboardSection>

      {/* Section 3: Document Classification */}
      <DashboardSection
        id="classification"
        title="Document Classification"
        description="Monitor the tiered classification system that routes documents to clients"
      >
        <ClassificationMetricsCards
          data={data?.classificationSummary}
          isLoading={isLoading}
        />
        <div className="grid gap-4 md:grid-cols-2">
          <ClassificationTierChart
            data={data?.classificationSummary?.byTier}
            isLoading={isLoading}
          />
          <RadialProgress
            title="Auto-Classification Rate"
            value={
              data?.classificationSummary?.matchRate
                ? data.classificationSummary.matchRate / 100
                : undefined
            }
            isLoading={isLoading}
            format="percent"
            thresholds={{
              warning: 0.7,
              critical: 0.5,
              inverse: false,
            }}
            tooltip={METRIC_DESCRIPTIONS.classificationMatchRate}
          />
        </div>
      </DashboardSection>

      {/* Section 4: Cost & Usage */}
      <DashboardSection
        id="cost-usage"
        title="Cost & Usage"
        description="Track LLM token consumption for cost optimization"
      >
        <div className="max-w-2xl">
          <TokenUsageChart
            inputData={data?.timeSeries.inputTokens}
            outputData={data?.timeSeries.outputTokens}
            summary={data?.tokenSummary}
            isLoading={isLoading}
          />
        </div>
      </DashboardSection>

      {/* Section 5: Data Ingestion */}
      <DashboardSection
        id="data-ingestion"
        title="Data Ingestion"
        description="Monitor document ingestion from webhooks and sync jobs"
      >
        <IngestionMetricsCards
          data={data?.ingestionSummary}
          isLoading={isLoading}
        />
        <div className="grid gap-4 md:grid-cols-2">
          <IngestionChart
            data={data?.ingestionBySource}
            isLoading={isLoading}
          />
          <SyncOutcomesChart
            data={data?.syncOutcomesBySource}
            isLoading={isLoading}
          />
        </div>
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-3">
            Sync Worker Performance
          </h3>
          <SyncWorkerMetricsCards
            data={data?.syncWorkerSummary}
            isLoading={isLoading}
          />
        </div>
      </DashboardSection>
    </div>
  );
}
